import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getListedBonds, getBondscannerIsinConfig } from './useListedBonds';
import { ListedBond } from './bonds.types';
import RMCard from '../../Deals/RMCard';
import BondsFaqs from './BondsFaq';
import { formatAmountWithText } from './constants';

const formatIN = (num: number) => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return num.toLocaleString('en-IN');
};

const formatMaturity = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StateTag = ({ state }: { state: ListedBond['state'] }) => {
  if (state === 'LIVE') {
    return (
      <span className="bg-[#E6F0E8] text-green-800 text-[10px] font-semibold rounded-md px-2 py-1 tracking-wide">
        LIVE
      </span>
    );
  }
  if (state === 'COMING_SOON') {
    return (
      <span className="bg-[#E6EEF7] text-blue-700 text-[10px] font-semibold rounded-md px-2 py-1 tracking-wide">
        COMING SOON
      </span>
    );
  }
  return null;
};

function ListedBondsPage() {
  const navigate = useNavigate();
  const [bonds, setBonds] = React.useState<ListedBond[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);

  const [configIsins, setConfigIsins] = React.useState<Set<string>>(new Set());
  const [sellYieldMinByIsin, setSellYieldMinByIsin] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    let isMounted = true;
    const fetchBonds = async () => {
      try {
        setIsLoading(true);
        setIsError(false);
        // Fetch listed bonds and the bondscanner ISIN config in parallel.
        const [result, isinConfig] = await Promise.all([
          getListedBonds(),
          getBondscannerIsinConfig(),
        ]);
        if (isMounted) {
          setBonds(result?.data?.data || []);
          const isins = isinConfig?.isins || [];
          setConfigIsins(new Set(isins.map((item) => item.isin)));
          setSellYieldMinByIsin(
            isins.reduce(
              (acc, item) => {
                acc[item.isin] = item.sellYieldMin;
                return acc;
              },
              {} as Record<string, number>,
            ),
          );
        }
      } catch (error) {
        if (isMounted) setIsError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchBonds();
    return () => {
      isMounted = false;
    };
  }, []);

  // Only show bonds whose ISIN is present in both v1/issuer/ipc and the
  // bondscanner ISIN config.
  const deals = bonds.filter(
    (deal) => (deal.state === 'LIVE' || deal.state === 'COMING_SOON') && configIsins.has(deal.isin),
  );
  console.log(deals, 'deals');
  const goToDeal = (deal: ListedBond) => {
    navigate(`/listed-bonds/${deal.isin}/${deal.id}`);
  };

  return (
    <div className="my-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-8 min-h-screen relative">
        <div>
          <div className="flex items-center gap-4 mb-3">
            <h1 className="text-xl font-semibold text-[#152420]">Listed Bonds</h1>
          </div>
          <p className="text-base text-stone-500 leading-relaxed mb-3">
            Explore listed bonds available for investment. Each bond offers a fixed yield with
            periodic payouts. Select a bond to view its details.
          </p>
        </div>

        {isLoading && <p className="text-base text-stone-500">Loading bonds...</p>}

        {!isLoading && isError && (
          <p className="text-base text-red-500">
            Something went wrong while fetching bonds. Please try again.
          </p>
        )}

        {!isLoading && !isError && deals.length === 0 && (
          <p className="text-base text-stone-500">No bonds available right now.</p>
        )}

        {!isLoading && !isError && deals.length > 0 && (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="w-full lg:w-[65%]">
              <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                {deals.map((deal) => (
                  <div
                    key={deal.id}
                    className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm cursor-pointer transition-all duration-150 hover:border-zinc-400"
                    onClick={() => goToDeal(deal)}
                    tabIndex={0}
                    role="button"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') goToDeal(deal);
                    }}
                    aria-label={`View deal ${deal.issuerName}`}
                  >
                    <div className="flex items-center gap-4 px-6 py-5 bg-white">
                      <img
                        src={deal.issuerLogoUrl}
                        alt={deal.issuerName}
                        className="w-12 h-12 rounded object-contain bg-white border border-zinc-200"
                      />
                      <div className="flex-1">
                        <div className="text-lg font-semibold text-[#23272F]">
                          {deal.issuerName}
                        </div>
                        <div className="text-sm text-[#6B6B6B]">{deal.isin}</div>
                      </div>
                      <StateTag state={deal.state} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-[#F5F5F3] px-6 py-4 border-t border-zinc-200">
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-stone-600 tracking-wide mb-1">
                          YIELD
                        </span>
                        <span className="text-base font-semibold text-green-700">
                          {sellYieldMinByIsin[deal.isin]}%
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-stone-600 tracking-wide mb-1">
                          MIN INVESTMENT
                        </span>
                        <span className="text-base font-semibold text-[#23272F]">
                          ~₹{formatAmountWithText(deal.minInvestmentAmount)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-stone-600 tracking-wide mb-1">
                          RATING
                        </span>
                        <span className="text-base font-semibold text-[#23272F]">
                          {deal.ratings || '-'}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-stone-600 tracking-wide mb-1">
                          MATURITY
                        </span>
                        <span className="text-base font-semibold text-[#23272F]">
                          {formatMaturity(deal.maturityDate)}
                        </span>
                        {deal.payoutFrequency && (
                          <span className="text-xs text-stone-500 mt-0.5">
                            {deal.payoutFrequency}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full lg:w-[35%]">
              <RMCard />
              <div className="mt-8">
                <p className="font-medium">Frequently asked questions</p>
                <BondsFaqs />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ListedBondsPage;
